import React, { memo, type ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import {
  ENTITLEMENT_DETAILS_TABS,
  INHERENT_RISKS_INI_CALL,
  testIds,
} from '../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { globalActions } from '../../../../../../shared/store/actions';
import { detailsActions } from '../../../../data-store/detailsSlice';
import { useEntitlementDetails } from '../../../../hooks';
import {
  useFiltersInitialization,
  useGetSortMenu,
  useInherentRisksColumnPicker,
} from '../hooks';
import usePageTracker from '../hooks/use-pagetracker';

import InherentRisksControls from './InherentRisksControls';
import InherentRisksList from './InherentRisksList';
import InherentRisksSidePanel from './InherentRisksSidePanel';

import './InherentRisksTab.css';

/**
 * InherentRisksTab Component
 *
 * Main container component for the Inherent Risks tab in entitlement details.
 * Orchestrates initialization of columns and manages layout including
 * controls, list view, and sidepanel functionality with responsive design and loading states.
 *
 * @component
 * @returns {React.ReactElement} Main container with header, controls, list, and sidepanel
 */
function InherentRisksTab(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Initial query hooks
  const { updateTracker } = (
    usePageTracker as (keys: string[]) => {
      updateTracker: (key: string, value: boolean) => void;
    }
  )(Object.values(INHERENT_RISKS_INI_CALL));
  const entitlementDetails = useEntitlementDetails();
  const { initializeAdvancedFilters } = (
    useFiltersInitialization as () => {
      initializeAdvancedFilters: () => Promise<boolean>;
    }
  )();
  const { initializeViewColumns } = (
    useInherentRisksColumnPicker as () => {
      initializeViewColumns: () => Promise<boolean>;
    }
  )();

  const {
    user: { isMenuPinned },
    aag: {
      details: {
        activeTab,
        entitlementRisks: {
          sidepanel: { isSidePanelVisible },
          controls: { sortInAction },
        },
      },
      global: { isGlobalLoading },
    },
  } = useTypedSelector((state) => state);
  const riskIds = entitlementDetails.entitlementDetails?.riskIds;
  const entKeys = entitlementDetails.entitlementDetails?.entitlementValueKey;

  // Enable sort hook
  useGetSortMenu(sortInAction);

  // Close sidepanel when switching away from risks tab
  useEffect(() => {
    if (
      activeTab &&
      activeTab !== ENTITLEMENT_DETAILS_TABS.TAB_RISKS &&
      isSidePanelVisible
    ) {
      dispatch(
        detailsActions.setEntitlementRisksSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: { current: { riskId: 0 } },
        })
      );
    }
  }, [activeTab, isSidePanelVisible, dispatch]);

  // Initialize filters and columns on mount with page tracker
  useEffect(() => {
    const initializeFiltersAndColumns = async () => {
      try {
        dispatch(globalActions.setIsGlobalLoading(true));

        // Order is important
        const response: boolean[] = await Promise.all([
          initializeAdvancedFilters(),
          initializeViewColumns(),
        ]);

        // Raise global error if any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Update tracker for each initialization call
        Object.values(INHERENT_RISKS_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      } catch (error) {
        dispatch(globalActions.setGlobalError({ state: true }));
        Object.values(INHERENT_RISKS_INI_CALL).forEach((callValue) => {
          updateTracker(callValue, false);
        });
      } finally {
        dispatch(globalActions.setIsGlobalLoading(false));
      }
    };

    initializeFiltersAndColumns().catch(() => {
      // Error handling is already done inside the function
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const risksTabClasses = useMemo(
    () =>
      cx(
        'Sod-InherentRisksTab',
        isMenuPinned && 'Sod-InherentRisksTab--isMenuPinned'
      ),
    [isMenuPinned]
  );

  const listAsideClasses = useMemo(
    () =>
      cx(
        'Sod-InherentRisksTab-main',
        isSidePanelVisible && 'Sod-InherentRisksTab-main--isSidePanelOpen'
      ),
    [isSidePanelVisible]
  );

  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return (
        <Box className='Sod-Violations-loaderWrapper'>
          <Loader kind='dots' format='inline' color='brand' />
        </Box>
      );
    }

    if (entitlementDetails.isError) {
      return (
        <Box className='Sod-SidePanelSummary-loaderWrapper'>
          <Typography kind='body2'>
            {l('Error loading Risks details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              entitlementDetails.refetch().catch(() => {
                // Error handling is managed by the hook
              });
            }}>
            {l('Retry')}
          </Button>
        </Box>
      );
    }

    return (
      <InherentRisksList
        riskIds={riskIds ?? []}
        entKeys={entKeys ? [entKeys] : []}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalLoading, entitlementDetails, l]);

  return (
    <Box
      className={risksTabClasses}
      dataTestId={testIds.SOD_ENTITLEMENT_TAB_RISKS}>
      <Box tag='section' className={listAsideClasses}>
        {/* Controls */}
        {!isGlobalLoading && !entitlementDetails.isError && (
          <Box className='Sod-InherentRisksTab-listAndControls'>
            <Box className='Sod-InherentRisksTab-controlsWrapper'>
              <InherentRisksControls />
            </Box>
          </Box>
        )}
        {/* Main scroll area */}
        <Box className='Sod-InherentRisksTab-scrollArea'>
          {/* List */}
          {renderContent}
        </Box>
        {/* Risks Sidepanel */}
        <Box className='Sod-InherentRisksTab-asides'>
          <InherentRisksSidePanel entKeys={entKeys ? [entKeys] : []} />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(InherentRisksTab);
